import { motion } from "framer-motion";
import { MessageCircle, Search, PenTool, BarChart3, Image, CalendarDays, Wrench, MoreHorizontal, Mic } from "lucide-react";
import NexiLivingOrb from "./NexiLivingOrb";

const actionButtons = [
  { icon: MessageCircle, label: "Chat", color: "from-cyan-500 to-blue-600" },
  { icon: Search, label: "Research", color: "from-violet-500 to-purple-600" },
  { icon: PenTool, label: "Create", color: "from-pink-500 to-rose-600" },
  { icon: BarChart3, label: "Analyze", color: "from-emerald-500 to-teal-600" },
  { icon: Image, label: "Images", color: "from-amber-500 to-orange-600" },
  { icon: CalendarDays, label: "Plan", color: "from-blue-500 to-indigo-600" },
  { icon: Wrench, label: "Tools", color: "from-slate-500 to-gray-600" },
  { icon: MoreHorizontal, label: "More", color: "from-fuchsia-500 to-pink-600" }
];

/**
 * NexiLivingHome - Empty state with orb and action grid
 * Fixed: All buttons now properly respond to touch/click
 */
const NexiLivingHome = ({ onSelectAction, onOrbClick, userName, voiceState = "idle", isListening = false, voiceSupported = true }) => {
  // Determine status text based on voice state
  const getStatusText = () => {
    if (!voiceSupported) {
      return "Type below to chat";
    }
    switch (voiceState) {
      case "listening":
        return "Listening...";
      case "processing":
        return "Processing...";
      case "error":
        return "Tap to try again";
      default:
        return "Tap to speak";
    }
  };

  const getSubText = () => {
    if (!voiceSupported) {
      return "Voice not available in app";
    }
    if (voiceState === "listening") {
      return "Speak now";
    }
    return "or type below";
  };

  // Handle action button click
  const handleActionClick = (label) => {
    if (onSelectAction) {
      onSelectAction(label);
    }
  };

  // Handle orb click
  const handleOrbClick = () => {
    if (onOrbClick) {
      onOrbClick();
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-4 py-6">
      {/* Main Orb */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="mb-5"
      >
        <NexiLivingOrb 
          onClick={handleOrbClick}
          isListening={voiceState === "listening" || isListening}
          isThinking={voiceState === "processing"}
          isError={voiceState === "error"}
        />
      </motion.div>

      {/* Status text */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="text-center mb-8"
      >
        <p className={`text-lg transition-colors duration-300 ${
          voiceState === "listening" ? "text-emerald-400" : 
          voiceState === "error" ? "text-red-400" :
          !voiceSupported ? "text-slate-500" :
          "text-slate-400"
        }`}>
          {getStatusText()}
        </p>
        <p className="text-slate-500 text-sm mt-1">
          {getSubText()}
        </p>
      </motion.div>

      {/* Action Buttons Grid - 4x2 */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="grid grid-cols-4 gap-4 sm:gap-6 w-full max-w-md px-2"
      >
        {actionButtons.map((action, index) => {
          const Icon = action.icon;
          return (
            <motion.button
              key={action.label}
              type="button"
              onClick={() => handleActionClick(action.label)}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.1 + index * 0.05 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex flex-col items-center gap-2 p-3 group touch-manipulation"
              style={{ WebkitTapHighlightColor: 'transparent' }}
              data-testid={`action-${action.label.toLowerCase()}`}
            >
              <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-2xl 
                bg-gradient-to-br ${action.color} 
                flex items-center justify-center 
                shadow-lg group-hover:shadow-xl group-active:shadow-md transition-all
                border border-white/10`}
              >
                <Icon size={24} className="text-white sm:w-7 sm:h-7" />
              </div>
              <span className="text-xs text-slate-400 group-hover:text-white group-active:text-white/80 transition-colors">
                {action.label}
              </span>
            </motion.button>
          );
        })}
      </motion.div>

      {/* Voice hint */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        className={`mt-8 flex items-center gap-2 text-xs ${voiceSupported ? 'text-slate-600' : 'text-slate-700'}`}
      >
        <Mic size={14} />
        <span>{voiceSupported ? "Voice commands enabled" : "Type to chat with Nexi"}</span>
      </motion.div>
    </div>
  );
};

export default NexiLivingHome;
